// Single home for invoice-import API failures -> Polish operator sentences.
//
// Backend codes come from docs/plans/invoice-workflow-api-contract.md; the
// backend has no i18n, so unknown codes fall back to the caller's Polish
// fallback via getApiErrorMessage and never render raw English prose.
// Blocking codes (stale state the operator must reload or resolve first) get
// sticky feedback through isBlockingInvoiceImportError.
import { getApiErrorMessage } from '../../utils/apiError';

const INVOICE_IMPORT_ERROR_KEYS: Record<string, string> = {
  INVALID_DELIVERY_SOURCE: 'invoices:workflow.api.invalidDeliverySource',
  INVALID_PZ_NUMBER: 'invoices:workflow.pzNumbers.invalid',
  PZ_NUMBER_EXISTS: 'invoices:workflow.pzNumbers.exists',
  INVOICE_KIND_NOT_RECEIVABLE: 'invoices:workflow.validation.standardRequired',
  INVOICE_INCOMPLETE: 'invoices:workflow.page.resolveIssues',
  INVOICE_FX_REQUIRED: 'invoices:workflow.validation.exchangeRateRequired',
  INVOICE_TOTALS_REQUIRED: 'invoices:workflow.validation.totalNetRequired',
  INVOICE_LINES_REQUIRED: 'invoices:workflow.validation.linesRequired',
  INVOICE_LINE_INCOMPLETE: 'invoices:workflow.page.resolveIssues',
  INVOICE_LINE_AMOUNTS_REQUIRED: 'invoices:workflow.validation.lineAmountsRequired',
  INVOICE_TOTALS_MISMATCH: 'invoices:workflow.validation.totalsInconsistent',
  INVOICE_SOURCE_REQUIRED: 'invoices:workflow.validation.lineMatchRequired',
  INVOICE_OVER_COVERAGE: 'invoices:workflow.api.overCoverage',
  // Decision 2026-10-27: the same document on another order is a confirmed part, on the same order a duplicate.
  INVOICE_DUPLICATE_DOCUMENT: 'invoices:workflow.api.duplicateDocument',
  INVOICE_APPROVAL_RETRY: 'invoices:workflow.api.approvalRetry',
  INVOICE_QUANTITY_MISMATCH: 'invoices:workflow.api.inconsistentQuantity',
  INVOICE_QUANTITY_UNRESOLVED: 'invoices:workflow.api.inconsistentQuantity',
  INVOICE_LANDED_COST_REQUIRES_RECONCILIATION: 'invoices:workflow.api.landedCostReconciliation',
  INVOICE_FREIGHT_ALLOCATION_UNRESOLVED: 'invoices:workflow.api.freightAllocationUnresolved',
  INCONSISTENT_QUANTITY_SET: 'invoices:workflow.api.inconsistentQuantity',
  // The receipt (PZ from an approved invoice) refuses a quantity pair that disagrees with the
  // dimensions (pzService createPZQuantityPairError); the copy names the receipt panel controls.
  PZ_QUANTITY_PAIR_INCONSISTENT: 'invoices:workflow.api.quantityPairInconsistent',
  SUPPLIER_MISMATCH: 'invoices:workflow.api.supplierMismatch',
  VARIANT_MISMATCH: 'invoices:workflow.api.variantMismatch',
  INVALID_RECOGNITION_MONTH: 'invoices:workflow.page.periodRequired',
  INVALID_VERSION: 'invoices:workflow.api.versionRequired',
  STALE_SOURCE: 'invoices:workflow.api.staleExtraction',
  EXTRACTION_IN_PROGRESS: 'invoices:workflow.api.extractionInProgress',
  PDF_TOO_MANY_PAGES: 'invoices:workflow.api.tooManyPages',
  PDF_IMAGE_TOO_LARGE: 'invoices:workflow.api.fileTooLarge',
  INVALID_FILE_SIGNATURE: 'invoices:workflow.api.invalidFile',

  INVALID_DRAFT: 'invoices:workflow.api.invalidDraft',
  INVALID_DOCUMENT_NUMBER: 'invoices:workflow.api.invalidDocumentNumber',
  INVALID_DOCUMENT_KIND: 'invoices:workflow.api.invalidDocumentKind',
  INVALID_INVOICE_TYPE: 'invoices:workflow.api.invalidDocumentKind',
  INVALID_CURRENCY: 'invoices:workflow.api.invalidCurrency',
  INVALID_DATE: 'invoices:workflow.api.invalidDate',
  INVALID_EXCHANGE_RATE: 'invoices:workflow.api.invalidExchangeRate',
  INVALID_MONEY: 'invoices:workflow.api.invalidMoney',
  INVALID_QUANTITY: 'invoices:workflow.api.invalidQuantity',
  INVALID_QUANTITY_SET: 'invoices:workflow.api.invalidQuantitySet',
  INVALID_UNIT: 'invoices:workflow.api.invalidUnit',
  INVALID_COMMERCIAL_PAIR: 'invoices:workflow.api.invalidCommercialPair',
  INVALID_VAT_RATE: 'invoices:workflow.api.invalidVatRate',
  INVALID_LINE: 'invoices:workflow.api.invalidLine',
  INVALID_LINES: 'invoices:workflow.api.invalidLines',
  INVALID_COUNTERPARTY: 'invoices:workflow.api.invalidCounterparty',
  DRAFT_NOT_FOUND: 'invoices:workflow.api.draftNotFound',
  SUPPLIER_ORDER_NOT_FOUND: 'invoices:workflow.api.supplierOrderNotFound',
  SUPPLIER_NOT_FOUND: 'invoices:workflow.api.supplierNotFound',
  ORDER_NOT_FOUND: 'invoices:workflow.api.orderNotFound',
  CORRECTED_INVOICE_NOT_FOUND: 'invoices:workflow.api.correctedInvoiceNotFound',
  FOREIGN_SUPPLIER_ORDER_LINE: 'invoices:workflow.api.foreignSourceLine',
  FOREIGN_ORDER_PRODUCT: 'invoices:workflow.api.foreignSourceLine',
  SOURCE_ORDER_MISMATCH: 'invoices:workflow.api.sourceOrderMismatch',
  DRAFT_NOT_EDITABLE: 'invoices:workflow.api.draftNotEditable',
  VERSION_CONFLICT: 'invoices:workflow.api.versionConflict',
  VERSION_REQUIRED: 'invoices:workflow.api.versionRequired',
  STALE_EXTRACTION: 'invoices:workflow.api.staleExtraction',
  MANUAL_EDITS_PRESENT: 'invoices:workflow.api.manualEditsPresent',
  EXTRACTION_SEQ_REQUIRED: 'invoices:workflow.api.extractionSeqRequired',
  EXTRACTION_NOT_FOUND: 'invoices:workflow.api.extractionNotFound',
  MISSING_FILE: 'invoices:workflow.api.missingFile',
  MISSING_IMAGES: 'invoices:workflow.api.missingFile',
  TOO_MANY_FILES: 'invoices:workflow.api.tooManyFiles',
  UNSUPPORTED_FILE_TYPE: 'invoices:workflow.api.unsupportedFileType',
  FILE_TOO_LARGE: 'invoices:workflow.api.fileTooLarge',
  INVALID_FILE: 'invoices:workflow.api.invalidFile',
  FILE_NOT_FOUND: 'invoices:workflow.api.fileNotFound',
  PDF_RASTERIZATION_UNAVAILABLE: 'invoices:workflow.api.pdfRasterizationUnavailable',
  PDF_RASTERIZATION_FAILED: 'invoices:workflow.api.pdfRasterizationFailed',
  EXTRACTION_NOT_CONFIGURED: 'invoices:workflow.api.extractionNotConfigured',
  EXTRACTION_TIMEOUT: 'invoices:workflow.api.extractionFailed',
  EXTRACTION_FAILED: 'invoices:workflow.api.extractionFailed',
  EXTRACTION_INVALID: 'invoices:workflow.api.extractionInvalid',
  EXTRACTION_SUM_MISMATCH: 'invoices:workflow.api.extractionSumMismatch',
  INVALID_IMAGE: 'invoices:workflow.api.invalidFile',
  // WP2: correction invoices and withdrawal.
  INVOICE_WITHDRAW_FORBIDDEN: 'invoices:workflow.corrections.errors.withdrawForbidden',
  INVOICE_WITHDRAW_COMPLAINT: 'invoices:workflow.corrections.errors.withdrawComplaint',
  INVALID_WITHDRAWAL_REASON: 'invoices:workflow.corrections.errors.reasonRequired',
  WITHDRAWAL_MONTH_BEFORE_INVOICE: 'invoices:workflow.corrections.errors.withdrawMonthBefore',
  INVOICE_WITHDRAWN: 'invoices:workflow.corrections.errors.withdrawn',
  CORRECTION_KIND_REQUIRED: 'invoices:workflow.corrections.errors.kindRequired',
  // Advance / settlement sales invoices (decision 2026-10-20-zaliczki-i-faktura-rozliczeniowa.md).
  ADVANCE_ORDER_REQUIRED: 'invoices:workflow.amountInvoice.errors.orderRequired',
  ADVANCE_LINES_NOT_ALLOWED: 'invoices:workflow.amountInvoice.errors.linesNotAllowed',
  ADVANCE_AMOUNT_INVALID: 'invoices:workflow.amountInvoice.errors.amountInvalid',
  ADVANCE_MIXED_WITH_INVOICE: 'invoices:workflow.amountInvoice.errors.mixedWithInvoice',
  SETTLEMENT_ADVANCE_REQUIRED: 'invoices:workflow.amountInvoice.errors.settlementAdvanceRequired',
  INVOICE_ORDER_HAS_ADVANCES: 'invoices:workflow.amountInvoice.errors.orderHasAdvances',
  CORRECTION_OF_AMOUNT_DOCUMENT: 'invoices:workflow.amountInvoice.errors.correctionNotAllowed',
  ADVANCE_WITHDRAW_SETTLEMENT_FIRST: 'invoices:workflow.amountInvoice.errors.withdrawSettlementFirst',
  ADVANCE_AFTER_SETTLEMENT: 'invoices:workflow.amountInvoice.errors.advanceAfterSettlement',
  // WP15: complaint correction („Reklamacja”).
  COMPLAINT_CORRECTION_ONLY: 'invoices:workflow.corrections.errors.complaintCorrectionOnly',
  COMPLAINT_LINE_NOT_ON_ORDER: 'invoices:workflow.corrections.errors.complaintLineNotOnOrder',
  INVALID_COMPLAINT_QUANTITY: 'invoices:workflow.corrections.errors.complaintQuantity',
  INVALID_COMPLAINT_LINES: 'invoices:workflow.corrections.errors.complaintLineNotOnOrder',
  COMPLAINT_LINES_REQUIRED: 'invoices:workflow.corrections.errors.complaintLinesRequired',
  COMPLAINT_QUANTITY_MISMATCH: 'invoices:workflow.corrections.errors.complaintQuantityMismatch',
  COMPLAINT_QUANTITY_ABOVE_INVOICE: 'invoices:workflow.corrections.errors.complaintQuantityAboveInvoice',
  COMPLAINT_WHOLE_LINE_UNSUPPORTED: 'invoices:workflow.corrections.errors.complaintWholeLine',
  COMPLAINT_LINE_NOT_INVOICED: 'invoices:workflow.corrections.errors.complaintNotInvoiced',
  COMPLAINT_NET_INCREASED: 'invoices:workflow.corrections.errors.complaintNetIncreased',
  COMPLAINT_UNIT_UNRESOLVED: 'invoices:workflow.corrections.errors.complaintUnit',
  CORRECTION_CURRENCY_MISMATCH: 'invoices:workflow.corrections.errors.currencyMismatch',
  CORRECTION_MONTH_BEFORE_INVOICE: 'invoices:workflow.corrections.errors.monthBefore',
  CORRECTION_OF_CORRECTION: 'invoices:workflow.corrections.errors.correctionOfCorrection',
  INVOICE_NOT_APPROVED: 'invoices:workflow.corrections.errors.notApproved',
  INVOICE_NOT_RECOGNIZED: 'invoices:workflow.corrections.errors.notRecognized',
};

const INVOICE_IMPORT_BLOCKING_CODES = new Set([
  'INVOICE_LANDED_COST_REQUIRES_RECONCILIATION',
  'INVOICE_FREIGHT_ALLOCATION_UNRESOLVED',
  'DRAFT_NOT_FOUND',
  'DRAFT_NOT_EDITABLE',
  'VERSION_CONFLICT',
  'STALE_EXTRACTION',
  'MANUAL_EDITS_PRESENT',
  'FOREIGN_SUPPLIER_ORDER_LINE',
  'FOREIGN_ORDER_PRODUCT',
  'SOURCE_ORDER_MISMATCH',
]);

export function isBlockingInvoiceImportError(error: any): boolean {
  const code = error?.response?.data?.code;
  return typeof code === 'string' && INVOICE_IMPORT_BLOCKING_CODES.has(code);
}

export function getInvoiceImportErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  fallbackParams?: Record<string, unknown>,
): string {
  return getApiErrorMessage(error, t, fallbackKey, INVOICE_IMPORT_ERROR_KEYS, fallbackParams);
}

/**
 * The Polish sentence for a known code only (null otherwise). Screens that still show the
 * backend's own Polish prose for other codes (the receipt panel) use it so a mapped code never
 * falls back to that prose.
 */
export function getKnownInvoiceImportErrorMessage(error: any, t: any): string | null {
  const code = error?.response?.data?.code;
  return typeof code === 'string' && INVOICE_IMPORT_ERROR_KEYS[code] ? t(INVOICE_IMPORT_ERROR_KEYS[code]) : null;
}
